import React from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PricingCard } from './PricingCard';
import { CreditPackage, PaymentService } from '@aaas/shared';

interface CreditPackagesGridProps {
  packages: CreditPackage[];
  loading: boolean;
  error: string | null;
  onSelectVariant: (variantId: number) => void;
  processingVariantId?: number | null;
}

export const CreditPackagesGrid: React.FC<CreditPackagesGridProps> = ({
  packages,
  loading,
  error,
  onSelectVariant,
  processingVariantId = null,
}) => {
  const { t } = useTranslation('portal');
  // Find most popular variant
  const mostPopularVariantId = PaymentService.findMostPopularPackage(packages);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>{t('creditPackages.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3 text-red-600">
          <AlertCircle className="w-6 h-6" />
          <div>
            <div className="font-medium">{t('creditPackages.loadFailed')}</div>
            <div className="text-sm text-red-500">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">
          <div className="font-medium">{t('creditPackages.noPackagesHeading')}</div>
          <div className="text-sm">{t('creditPackages.noPackagesCheckback')}</div>
        </div>
      </div>
    );
  }

  // Get all variants from all packages
  const allVariants = packages.flatMap((pkg) => pkg.variants);

  if (allVariants.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">
          <div className="font-medium">{t('creditPackages.noPackagesHeading')}</div>
          <div className="text-sm">{t('creditPackages.noPackagesUnavailable')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('creditPackages.heading')}</h3>
        <p className="text-gray-600">{t('creditPackages.subheading')}</p>
      </div>

      {/* Packages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages.map((pkg) =>
          pkg.variants.map((variant) => (
            <PricingCard
              key={variant.id}
              variant={variant}
              package={pkg}
              isPopular={variant.id === mostPopularVariantId}
              onSelect={onSelectVariant}
              isLoading={processingVariantId === variant.id}
              disabled={processingVariantId !== null && processingVariantId !== variant.id}
            />
          ))
        )}
      </div>

      {/* Footer Info */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center space-x-8 text-sm text-gray-500">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span>{t('creditPackages.featureSecure')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span>{t('creditPackages.featureInstant')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
            <span>{t('creditPackages.featureNeverExpires')}</span>
          </div>
        </div>

        <p className="text-xs text-gray-400 max-w-2xl mx-auto">{t('creditPackages.footer')}</p>
      </div>
    </div>
  );
};
